import {useRef} from 'react';

import {get} from 'lodash';
import styled from 'styled-components';
import _Autosuggest from 'react-autosuggest';

import {LocationTypes, APARTMENT_MAX_LENGTH, ADDRESS_PHONE_INPUT_PROPS} from '~/shared/consts/addressConsts';
import {FormInput} from '~/shared/components/FormField';

const Autosuggest = styled(_Autosuggest).attrs({
  wrapperStyle: {
    zIndex: 2,
  },
})``;

const validateCityName = ({cityNameAutocomplete, cityName, cityId, t, disabled}) => {
  if (disabled || cityId) {
    return;
  }

  const items = Object.values(cityNameAutocomplete || {}).flat() || [];

  const isListIsEmptyAndCityNameIsntFromSelection = !items.some(i => i.name === cityName)
    ? t('the_value_must_be_in_the_list')
    : undefined;

  return isListIsEmptyAndCityNameIsntFromSelection;
};

const RenderCityName = ({
  value,
  placeholder,
  required,
  cityNameAutocomplete,
  disabled,
  fetchCityNamesIfNeeded,
  form,
}) => {
  const lastestItems = useRef([]);
  const items = get(cityNameAutocomplete, value) || lastestItems.current;
  lastestItems.current = items;

  const onChange = (event, {newValue}) => {
    form.batch(() => {
      form.change('streetName', '');
      form.change('cityName', newValue);
    });
  };

  const onSuggestionSelected = (event, {suggestion}) => {
    form.batch(() => {
      form.change('cityId', String(suggestion.id));
      form.change('cityName', suggestion.name);
    });
  };

  return (
    <Autosuggest
      value={value}
      renderInputComponent={({ref, ...props}) => <FormInput ref={ref} {...props} />}
      suggestions={items}
      onSuggestionsFetchRequested={({value: inputValue}) => fetchCityNamesIfNeeded(inputValue)}
      onSuggestionsClearRequested={() => null}
      getSuggestionValue={item => item.name}
      renderSuggestion={item => item.name}
      onSuggestionSelected={onSuggestionSelected}
      inputProps={{
        value,
        onChange,
        disabled,
        placeholder,
        required,
        'data-test-id': 'editAddressCityInput',
      }}
    />
  );
};

const validateStreetName = ({generalDisabled: disabled, streetId, cityId, streetName, streetNameAutocomplete, t}) => {
  if (disabled || streetId) {
    return undefined;
  }

  const items = get(streetNameAutocomplete, cityId) || [];

  const noItemsOrNoStreetName = items.length === 0 || !items.find(street => street.name === streetName);

  return noItemsOrNoStreetName ? t('the_value_must_be_in_the_list') : undefined;
};

const RenderStreetName = ({
  value,
  disabled,
  placeholder,
  required,
  cityId,
  streetNameAutocomplete,
  fetchStreetNamesIfNeeded,
  form,
}) => {
  const lastestItems = useRef([]);
  const items = get(streetNameAutocomplete, cityId) || lastestItems.current;
  lastestItems.current = items;

  const onChange = (event, {newValue}) => {
    event.preventDefault();

    form.batch(() => {
      form.change('streetName', newValue);
      form.change('streetId', null);
    });
  };

  const onSuggestionSelected = (event, {suggestion}) => {
    event.preventDefault();

    form.batch(() => {
      form.change('streetName', suggestion.name);
      form.change('streetId', String(suggestion.id));
    });
  };

  return (
    <Autosuggest
      suggestions={items}
      renderInputComponent={({ref, ...props}) => <FormInput {...props} ref={ref} />}
      onSuggestionsFetchRequested={({value: inputValue}) => fetchStreetNamesIfNeeded(inputValue, cityId)}
      onSuggestionsClearRequested={() => null}
      getSuggestionValue={item => item.name}
      renderSuggestion={item => item.name}
      onSuggestionSelected={onSuggestionSelected}
      inputProps={{
        value,
        onChange,
        disabled,
        placeholder,
        required,
        'data-test-id': 'editAddressStreetInput',
      }}
    />
  );
};

export const getFields = props => {
  const {
    form,
    values: {
      cityId: cityIdFromValues,
      cityName: cityNameFromValues,
      streetId: streetIdFromValues,
      addressId,
      locationType,
    },
    isCompanyAddress,
    cityNameAutocomplete,
    streetNameAutocomplete,
    fetchStreetNamesIfNeeded,
    fetchCityNamesIfNeeded,
    t,
  } = props;

  const generalDisabled = isCompanyAddress || !addressId;

  return [
    [
      {
        name: 'cityName',
        placeholder: t('city'),
        required: true,
        disabled: generalDisabled,
        cityName: cityNameFromValues,
        render: RenderCityName,
        validator: cityName =>
          validateCityName({cityNameAutocomplete, cityName, cityId: cityIdFromValues, disabled: generalDisabled, t}),
        ...{
          form,
          cityNameAutocomplete,
          fetchCityNamesIfNeeded,
        },
      },
      {
        name: 'streetName',
        placeholder: t('street'),
        required: true,
        disabled: generalDisabled,
        render: RenderStreetName,
        validator: streetName =>
          validateStreetName({
            generalDisabled,
            streetId: streetIdFromValues,
            cityId: cityIdFromValues,
            streetName,
            streetNameAutocomplete,
            t,
          }),
        ...{
          form,
          cityId: cityIdFromValues,
          fetchStreetNamesIfNeeded,
          streetNameAutocomplete,
        },
      },
    ],
    [
      {
        name: 'houseNumber',
        placeholder: t('house_number'),
        type: 'textWithTelInput',
        required: true,
        inputProps: {
          disabled: generalDisabled,
        },
      },
      {
        name: 'phoneNumber',
        placeholder: t('phone_number'),
        ...ADDRESS_PHONE_INPUT_PROPS,
      },
    ],
    [
      {
        name: 'apartmentNumber',
        placeholder: t('apartment_number'),
        type: 'textWithTelInput',
        inputProps: {
          disabled: isCompanyAddress,
          maxLength: APARTMENT_MAX_LENGTH,
        },
      },
      {
        name: 'entrance',
        placeholder: t('entrance'),
        inputProps: {
          disabled: isCompanyAddress,
        },
      },
      {
        name: 'floor',
        placeholder: t('floor'),
        type: 'textWithTelInput',
        inputProps: {
          disabled: isCompanyAddress,
        },
      },
    ],
    [
      {
        name: 'nameOnDoor',
        placeholder: t('name_on_door'),
      },
      {
        name: 'locationType',
        placeholder: t('location_type'),
        type: 'select',
        inputProps: {
          options: [
            {value: LocationTypes.RESIDENTIAL, text: t(LocationTypes.RESIDENTIAL)},
            {value: LocationTypes.BUSINESS, text: t(LocationTypes.BUSINESS)},
            {value: LocationTypes.HOTEL, text: t(LocationTypes.HOTEL)},
          ],
        },
      },
    ],
    [
      {
        name: 'locationName',
        placeholder: t('hotel_name'),
        required: true,
        hide: locationType !== LocationTypes.HOTEL,
      },

      {
        name: 'locationName',
        placeholder: t('business_name'),
        required: true,
        hide: locationType !== LocationTypes.BUSINESS,
      },
    ],
  ];
};
